import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { GradientSpinner } from "../common/GradientSpinner";
import type {
  MyCandidateFromAPI,
  CandidateDetail,
  GreetingResponse,
  CandidateStatusEnum,
} from "../../types/candidate";
import { CandidateStatusEnum as CandidateStatusValues } from "../../types/candidate";
import { apiClient, ApiClientError } from "../../lib/api-client";
import { env } from "../../lib/env";
import { cn, toTitleCase } from "../../lib/utils";
import { Icon } from "../ui/Icon";
import LetterLinear from "~icons/solar/letter-linear";
import PhoneLinear from "~icons/solar/phone-linear";
import MapPointLinear from "~icons/solar/map-point-linear";
import BuildingsLinear from "~icons/solar/buildings-linear";
import { getInitials, ensureHttps } from "./candidate-utils";
import {
  ScoreDisplay,
  StatusBadge,
  AttributesSection,
  MatchToJobCard,
  WorkExperienceCard,
  EducationCard,
  AnimatedSection,
  TabBar,
  CvViewer,
} from "./shared-candidate-components";
import { ActivitiesLog } from "./ActivitiesLog";
import { GreetingPreviewModal } from "./GreetingPreviewModal";
import { CandidateStatusDropdown } from "./CandidateStatusDropdown";
import { CandidateAvatarImage } from "./CandidateAvatarImage";

// Re-export CollapsibleContent so any existing external imports keep working.
export { CollapsibleContent } from "./shared-candidate-components";

// ---------------------------------------------------------------------------
// Snapshot helpers — when a project-scoped GET returns candidate_snapshot,
// the snapshot was frozen at HM add-to-project time. We render that view
// instead of the live candidate so HM decisions don't shift under their feet.
//
// BE shape (buildCandidateSnapshot):
//   { candidate: {...full doc}, employment_history: [...], education: [...], skills: [...] }
// The merge reads from those keys directly — flattening the nested `candidate`
// object's scalars onto the detail shape.
// ---------------------------------------------------------------------------

function mergeSnapshot(
  live: CandidateDetail,
  hasJdPath: boolean
): CandidateDetail {
  if (!hasJdPath || !live.candidate_snapshot) return live;
  const snap = live.candidate_snapshot;
  const cand = snap.candidate ?? {};
  return {
    ...live,
    full_name: cand.full_name ?? live.full_name,
    email: cand.email ?? live.email,
    phone: cand.phone ?? live.phone,
    address: cand.address ?? live.address,
    current_job_title: cand.current_job_title ?? live.current_job_title,
    linkedin_profile_url:
      cand.linkedin_profile_url ?? live.linkedin_profile_url,
    avatar_url: cand.avatar_url ?? live.avatar_url,
    summary: cand.summary ?? live.summary,
    profile_summary: cand.profile_summary ?? live.profile_summary,
    employment_history_items:
      snap.employment_history ?? live.employment_history_items,
    education_items: snap.education ?? live.education_items,
    skills: snap.skills ?? live.skills,
  };
}

function SnapshotBanner({ asOf }: { asOf: string }) {
  let formatted = asOf;
  try {
    formatted = format(parseISO(asOf), "dd MMM yyyy");
  } catch {
    // keep raw string
  }
  return (
    <div
      role="status"
      className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      Showing snapshot as of <strong>{formatted}</strong>. Edits made to this
      candidate after that date are not reflected here.
    </div>
  );
}

// Snapshot feature is temporarily disabled (call sites commented in JSX below).
// Keep mergeSnapshot + SnapshotBanner defined — re-enable by uncommenting their
// call sites. The void references below satisfy noUnusedLocals without altering
// runtime behavior.
void mergeSnapshot;
void SnapshotBanner;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CandidateDetailContentProps {
  /** Candidate list-level data (has confidence_score, candidate_id, etc.) */
  candidate: MyCandidateFromAPI;
  /** JD path to enrich candidate with status fields (score, status, source). */
  jdPath?: string;
  /** Callback when a contact action is triggered */
  onContactAction?: (message: string) => void;
  /**
   * Callback when the user selects a new status from the dropdown.
   * Only called when `candidate.id` is present (CandidateStatus doc ID).
   */
  onStatusChange?: (newStatus: CandidateStatusEnum) => void;
  /** Whether a status update is in progress — shows a loading indicator */
  isStatusUpdating?: boolean;
  /** Optional className for the root container */
  className?: string;
}

// ---------------------------------------------------------------------------
// Tab Configuration
// ---------------------------------------------------------------------------

type TabId = "profile" | "resume" | "activities";

const TABS: { id: TabId; label: string }[] = [
  { id: "profile", label: "Profile" },
  { id: "resume", label: "Resume" },
  { id: "activities", label: "Activities Log" },
];

// ---------------------------------------------------------------------------
// Inline SVG Icons (modal-specific)
// ---------------------------------------------------------------------------

function LinkedInIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 21 21"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M18.3543 0H1.82542C0.817277 0 0 0.817277 0 1.82542V18.3542C0 19.3624 0.817277 20.1797 1.82542 20.1797H18.3542C19.3624 20.1797 20.1797 19.3624 20.1797 18.3542V1.82542C20.1797 0.817277 19.3624 0 18.3543 0ZM6.24445 17.4245C6.24445 17.7179 6.00663 17.9557 5.71323 17.9557H3.45194C3.15854 17.9557 2.92072 17.7179 2.92072 17.4245V7.94525C2.92072 7.65185 3.15854 7.41403 3.45194 7.41403H5.71323C6.00663 7.41403 6.24445 7.65185 6.24445 7.94525V17.4245ZM4.58259 6.52047C3.39616 6.52047 2.43435 5.55866 2.43435 4.37223C2.43435 3.1858 3.39616 2.22399 4.58259 2.22399C5.76901 2.22399 6.73082 3.1858 6.73082 4.37223C6.73082 5.55866 5.76907 6.52047 4.58259 6.52047ZM18.0619 17.4673C18.0619 17.737 17.8432 17.9557 17.5734 17.9557H15.1469C14.8772 17.9557 14.6585 17.737 14.6585 17.4673V13.021C14.6585 12.3577 14.853 10.1144 12.9251 10.1144C11.4296 10.1144 11.1263 11.6499 11.0654 12.3389V17.4673C11.0654 17.737 10.8467 17.9557 10.577 17.9557H8.23009C7.96036 17.9557 7.74166 17.737 7.74166 17.4673V7.90246C7.74166 7.63273 7.96036 7.41403 8.23009 7.41403H10.577C10.8467 7.41403 11.0654 7.63273 11.0654 7.90246V8.72946C11.6199 7.89728 12.444 7.25497 14.1986 7.25497C18.0841 7.25497 18.0619 10.885 18.0619 12.8795V17.4673Z"
        fill="currentColor"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Loading Skeleton
// ---------------------------------------------------------------------------

function SkeletonPulse({ className }: { className?: string }) {
  return (
    <div
      className={`motion-safe:animate-pulse rounded-lg bg-grey-50 ${className ?? ""}`}
      aria-hidden="true"
    />
  );
}

function CandidateDetailSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-label="Loading candidate details">
      {/* Tab bar skeleton */}
      <SkeletonPulse className="h-[44px] rounded-full" />

      {/* Profile card skeleton */}
      <div className="rounded-[20px] border border-black-50 p-5">
        <div className="flex flex-col items-center gap-4">
          <SkeletonPulse className="size-[89px] rounded-full" />
          <SkeletonPulse className="h-5 w-40" />
          <SkeletonPulse className="h-4 w-28" />
          <SkeletonPulse className="h-10 w-full rounded-full" />
        </div>
        <div className="mt-5 border-t border-bg-page pt-5">
          <div className="flex flex-col gap-4">
            <SkeletonPulse className="h-4 w-32" />
            <SkeletonPulse className="h-8 w-20" />
          </div>
        </div>
        <div className="mt-5 border-t border-bg-page pt-5">
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <SkeletonPulse className="size-5 rounded" />
                <div className="flex flex-col gap-1">
                  <SkeletonPulse className="h-3 w-12" />
                  <SkeletonPulse className="h-4 w-36" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Match card skeleton */}
      <div className="rounded-[20px] border border-black-50 p-5">
        <SkeletonPulse className="h-5 w-48" />
        <SkeletonPulse className="mt-3 h-20 w-full" />
      </div>

      {/* Work experience skeleton */}
      <div className="rounded-[20px] border border-black-50 p-5">
        <SkeletonPulse className="h-5 w-40" />
        <div className="mt-4 flex flex-col gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <SkeletonPulse key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile Widget Card
// ---------------------------------------------------------------------------

function DetailItem({
  icon,
  label,
  value,
  isLink,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | undefined;
  isLink?: boolean;
  href?: string;
}) {
  const displayValue = value || "N/A";

  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0 text-grey">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-body-sm text-grey">{label}</p>
        {isLink && href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate text-body-md font-medium text-link hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {displayValue}
          </a>
        ) : (
          <p className="truncate text-body-md font-medium text-black-500">
            {displayValue}
          </p>
        )}
      </div>
    </div>
  );
}

const CONTACT_MENU_ITEMS = [
  {
    id: "email" as const,
    label: "Send Email",
    icon: LetterLinear,
    iconType: "icon" as const,
    disabled: false,
  },
  {
    id: "call" as const,
    label: "Call or Text Message",
    icon: PhoneLinear,
    iconType: "icon" as const,
    disabled: true,
  },
  {
    id: "linkedin" as const,
    label: "LinkedIn InMail",
    icon: null,
    iconType: "linkedin-inmail" as const,
    disabled: true,
  },
] as const;

function LinkedInInMailIcon() {
  return (
    <span className="relative inline-flex size-6 items-center justify-center">
      {/* Send/letter icon */}
      <Icon icon={LetterLinear} size="md" />
      {/* Small LinkedIn badge */}
      <span className="absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-[#0A66C2]">
        <svg
          width="8"
          height="8"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M18.3543 0H1.82542C0.817277 0 0 0.817277 0 1.82542V18.3542C0 19.3624 0.817277 20.1797 1.82542 20.1797H18.3542C19.3624 20.1797 20.1797 19.3624 20.1797 18.3542V1.82542C20.1797 0.817277 19.3624 0 18.3543 0ZM6.24445 17.4245C6.24445 17.7179 6.00663 17.9557 5.71323 17.9557H3.45194C3.15854 17.9557 2.92072 17.7179 2.92072 17.4245V7.94525C2.92072 7.65185 3.15854 7.41403 3.45194 7.41403H5.71323C6.00663 7.41403 6.24445 7.65185 6.24445 7.94525V17.4245ZM4.58259 6.52047C3.39616 6.52047 2.43435 5.55866 2.43435 4.37223C2.43435 3.1858 3.39616 2.22399 4.58259 2.22399C5.76901 2.22399 6.73082 3.1858 6.73082 4.37223C6.73082 5.55866 5.76907 6.52047 4.58259 6.52047ZM18.0619 17.4673C18.0619 17.737 17.8432 17.9557 17.5734 17.9557H15.1469C14.8772 17.9557 14.6585 17.737 14.6585 17.4673V13.021C14.6585 12.3577 14.853 10.1144 12.9251 10.1144C11.4296 10.1144 11.1263 11.6499 11.0654 12.3389V17.4673C11.0654 17.737 10.8467 17.9557 10.577 17.9557H8.23009C7.96036 17.9557 7.74166 17.737 7.74166 17.4673V7.90246C7.74166 7.63273 7.96036 7.41403 8.23009 7.41403H10.577C10.8467 7.41403 11.0654 7.63273 11.0654 7.90246V8.72946C11.6199 7.89728 12.444 7.25497 14.1986 7.25497C18.0841 7.25497 18.0619 10.885 18.0619 12.8795V17.4673Z"
            fill="white"
          />
        </svg>
      </span>
    </span>
  );
}

function ContactNowDropdown({
  candidateId,
  projectId,
  candidateName,
  candidateEmail,
  canContact,
  onContactAction,
}: {
  candidateId: string;
  projectId: string;
  candidateName: string;
  candidateEmail?: string;
  canContact: boolean;
  onContactAction: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [loadingItem, setLoadingItem] = useState<string | null>(null);
  const [greetingModalOpen, setGreetingModalOpen] = useState(false);

  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Close dropdown and return focus to trigger
  const closeMenu = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Consolidated: click-outside and ESC handler
  useEffect(() => {
    if (!isOpen) return;

    function handleEvent(event: MouseEvent | KeyboardEvent) {
      if (event instanceof KeyboardEvent && event.key === "Escape") {
        closeMenu();
        return;
      }

      if (event instanceof MouseEvent) {
        const target = event.target as Node;
        if (
          menuRef.current &&
          !menuRef.current.contains(target) &&
          triggerRef.current &&
          !triggerRef.current.contains(target)
        ) {
          closeMenu();
        }
      }
    }

    document.addEventListener("mousedown", handleEvent);
    document.addEventListener("keydown", handleEvent);
    return () => {
      document.removeEventListener("mousedown", handleEvent);
      document.removeEventListener("keydown", handleEvent);
    };
  }, [isOpen, closeMenu]);

  // Auto-focus first menu item when opened
  useEffect(() => {
    if (isOpen) {
      // Defer focus to next frame so the menu is rendered
      requestAnimationFrame(() => {
        itemRefs.current[0]?.focus();
      });
    }
  }, [isOpen]);

  // Arrow-key navigation within the menu
  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const itemCount = CONTACT_MENU_ITEMS.length;
      let nextIndex = focusedIndex;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          nextIndex = (focusedIndex + 1) % itemCount;
          break;
        case "ArrowUp":
          event.preventDefault();
          nextIndex = (focusedIndex - 1 + itemCount) % itemCount;
          break;
        case "Home":
          event.preventDefault();
          nextIndex = 0;
          break;
        case "End":
          event.preventDefault();
          nextIndex = itemCount - 1;
          break;
        default:
          return;
      }

      setFocusedIndex(nextIndex);
      itemRefs.current[nextIndex]?.focus();
    },
    [focusedIndex]
  );

  const handleItemClick = useCallback(
    async (itemId: string) => {
      if (loadingItem) return;

      if (itemId === "email") {
        setIsOpen(false);
        triggerRef.current?.focus();
        setGreetingModalOpen(true);
        return;
      } else if (itemId === "linkedin") {
        setLoadingItem("linkedin");
        try {
          const res = await apiClient.post<GreetingResponse>(
            `/candidates/${candidateId}/greeting`,
            { type: "linkedin", project_id: projectId }
          );
          setIsOpen(false);
          triggerRef.current?.focus();
          void queryClient.invalidateQueries({
            queryKey: ["projects", projectId, "candidates"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["candidates", candidateId],
          });
          if (res.linkedin_url) {
            window.open(
              ensureHttps(res.linkedin_url),
              "_blank",
              "noopener,noreferrer"
            );
            onContactAction(res.suggested_message ?? "LinkedIn profile opened");
          } else {
            onContactAction("LinkedIn profile opened");
          }
        } catch (err) {
          setIsOpen(false);
          triggerRef.current?.focus();
          const message =
            err instanceof ApiClientError && err.errors.length > 0
              ? err.errors[0]
              : "Failed to open LinkedIn. Please try again.";
          onContactAction(message);
        } finally {
          setLoadingItem(null);
        }
      } else {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    },
    [onContactAction, candidateId, projectId, loadingItem]
  );

  return (
    <div className="relative mx-auto mt-5 w-[250px]">
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        disabled={!canContact}
        onClick={() => {
          if (!canContact) return;
          setIsOpen((prev) => !prev);
          setFocusedIndex(0);
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        className={cn(
          "flex h-10 w-full items-center justify-center rounded-full text-body-md font-bold text-white motion-safe:transition-[transform,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
          !canContact
            ? "cursor-not-allowed bg-grey opacity-50"
            : "cursor-pointer bg-gradient-to-r from-gradient-start to-gradient-end hover:opacity-90 active:scale-[0.97]"
        )}
      >
        Contact Now
      </button>

      {/* Dropdown menu — positioned absolutely within the relative wrapper.
          A portal to document.body would render BEHIND the native <dialog>
          top layer created by showModal(), so we keep this in-tree instead. */}
      {isOpen && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Contact options"
          onKeyDown={handleMenuKeyDown}
          className="absolute left-1/2 top-full z-50 mt-3 w-[283px] -translate-x-1/2 motion-safe:animate-[fade-in_150ms_ease-out] rounded-2xl border border-grey-100 bg-white p-2 shadow-[0px_8px_24px_-8px_rgba(12,12,13,0.2)]"
        >
          {/* Arrow pointing up */}
          <div
            className="absolute -top-[7px] left-1/2 -translate-x-1/2"
            aria-hidden="true"
          >
            <svg width="14" height="7" viewBox="0 0 14 7" fill="none">
              <path
                d="M7 0L13.5 7H0.5L7 0Z"
                fill="white"
                stroke="#E1E4EA"
                strokeWidth="1"
                strokeLinejoin="round"
              />
              {/* White fill to cover the bottom border line */}
              <rect x="1" y="6" width="12" height="2" fill="white" />
            </svg>
          </div>

          {/* Menu items */}
          {CONTACT_MENU_ITEMS.map((item, index) => {
            const isItemLoading = loadingItem === item.id;
            const isDisabled =
              item.disabled ||
              !!loadingItem ||
              (item.id === "email" && !candidateEmail);
            return (
              <button
                key={item.label}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={focusedIndex === index ? 0 : -1}
                disabled={isDisabled}
                onClick={() => !isDisabled && handleItemClick(item.id)}
                className={cn(
                  "flex w-full items-center gap-3.5 rounded-full px-2 py-2 text-sm font-medium motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  isDisabled
                    ? "cursor-default text-grey opacity-50"
                    : "cursor-pointer text-black-500 hover:bg-grey-50"
                )}
              >
                <span className="flex size-6 shrink-0 items-center justify-center">
                  {isItemLoading ? (
                    <GradientSpinner size={16} />
                  ) : item.iconType === "icon" && item.icon ? (
                    <Icon icon={item.icon} size="lg" />
                  ) : item.iconType === "linkedin-inmail" ? (
                    <LinkedInInMailIcon />
                  ) : (
                    <LinkedInIcon />
                  )}
                </span>
                {item.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Greeting preview modal — rendered inside the relative wrapper so it
          stays associated with the trigger, but <dialog> goes to the top layer
          automatically via showModal() so z-index stacking is not an issue. */}
      <GreetingPreviewModal
        open={greetingModalOpen}
        onClose={() => setGreetingModalOpen(false)}
        candidateId={candidateId}
        candidateName={candidateName}
        candidateEmail={candidateEmail}
        projectId={projectId}
        onSuccess={(msg) => {
          setGreetingModalOpen(false);
          void queryClient.invalidateQueries({
            queryKey: ["projects", projectId, "candidates"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["candidates", candidateId],
          });
          onContactAction(msg);
        }}
        onError={(msg) => {
          onContactAction(msg);
        }}
      />
    </div>
  );
}

function ProfileWidgetCard({
  data,
  candidate,
  onContactAction,
  onStatusChange,
  isStatusUpdating,
}: {
  data: CandidateDetail;
  candidate: MyCandidateFromAPI;
  onContactAction: (message: string) => void;
  onStatusChange?: (newStatus: CandidateStatusEnum) => void;
  isStatusUpdating?: boolean;
}) {
  // Prefer the fresh status from the detail API response over the stale list-level prop.
  const displayStatus = data.status ?? candidate.status;

  const scoreSource =
    data.initial_confidence_score ?? candidate.confidence_score;
  const initialScore = scoreSource
    ? Math.round(scoreSource.overall_score)
    : undefined;

  // Derive company from current_job_title or first employment entry
  const company =
    data.employment_history_items.length > 0
      ? data.employment_history_items[0].company_name
      : undefined;

  return (
    <div className="rounded-[20px] border border-black-50 bg-white p-5">
      {/* B1: Profile Header (centered) */}
      <div className="flex flex-col items-center gap-[15px]">
        {/* Avatar 89x89 */}
        <div className="flex size-[89px] shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end p-0.5">
          <CandidateAvatarImage
            src={data.avatar_url || candidate.avatar_url}
            alt={toTitleCase(data.full_name)}
            className="size-full rounded-full object-cover"
            fallback={
              <div className="flex size-full items-center justify-center rounded-full bg-white">
                <span className="bg-gradient-to-r from-gradient-start to-gradient-end bg-clip-text text-display-md font-bold text-transparent">
                  {getInitials(toTitleCase(data.full_name))}
                </span>
              </div>
            }
          />
        </div>
        {/* Name */}
        <div className="text-center">
          <h3 className="text-body-xl font-semibold text-black-500">
            {toTitleCase(data.full_name)}
          </h3>
          {data.current_job_title && (
            <p className="mt-1 text-body-md text-black-500/60">
              {toTitleCase(data.current_job_title)}
            </p>
          )}
          {company && (
            <div className="mt-0.5 flex items-center justify-center gap-1 text-body-md text-black-500/60">
              <Icon icon={BuildingsLinear} size="sm" className="shrink-0" />
              <span>{toTitleCase(company)}</span>
            </div>
          )}
        </div>
      </div>

      {/* B2: Contact Button with Dropdown */}
      <ContactNowDropdown
        candidateId={candidate.candidate_id}
        projectId={candidate.project_id}
        candidateName={toTitleCase(data.full_name)}
        candidateEmail={data.email ?? candidate.email}
        canContact={
          displayStatus === CandidateStatusValues.NEW ||
          displayStatus === CandidateStatusValues.CONTACTED
        }
        onContactAction={onContactAction}
      />

      {/* B3: Divider */}
      <div className="my-5 h-px bg-bg-page" aria-hidden="true" />

      {/* B4: Info Grid */}
      <div className="flex flex-col gap-2.5">
        {/* Status row */}
        <div className="flex items-center justify-between">
          <span className="text-body-md font-medium text-black-500/60">
            Status
          </span>
          {onStatusChange ? (
            <CandidateStatusDropdown
              value={displayStatus}
              onChange={onStatusChange}
              isUpdating={isStatusUpdating ?? false}
            />
          ) : (
            <StatusBadge status={displayStatus} />
          )}
        </div>

        {/* Initial Confidence Score — held behind FEATURE_CONFIDENCE_SCORE_UI */}
        {env.FEATURE_CONFIDENCE_SCORE_UI && (
          <ScoreDisplay label="Confidence Score" score={initialScore} />
        )}
      </div>

      {/* B5: Divider */}
      <div className="my-5 h-px bg-bg-page" aria-hidden="true" />

      {/* B6: Details Section */}
      <div className="flex flex-col gap-5">
        <h4 className="text-body-xl font-semibold text-secondary">Details</h4>
        <div className="flex flex-col gap-2.5">
          <DetailItem
            icon={<LinkedInIcon />}
            label="Linkedin URL"
            value={data.linkedin_profile_url ? "View Profile" : undefined}
            isLink={!!data.linkedin_profile_url}
            href={
              data.linkedin_profile_url
                ? ensureHttps(data.linkedin_profile_url)
                : undefined
            }
          />
          <DetailItem
            icon={<Icon icon={LetterLinear} size="md" />}
            label="Email"
            value={data.email}
          />
          <DetailItem
            icon={<Icon icon={PhoneLinear} size="md" />}
            label="Phone"
            value={data.phone}
          />
          <DetailItem
            icon={<Icon icon={MapPointLinear} size="md" />}
            label="Location"
            value={data.address}
          />
        </div>
      </div>

      {/* B7: Divider */}
      <div className="my-5 h-px bg-bg-page" aria-hidden="true" />

      {/* B8: Attributes/Skills Section */}
      <AttributesSection items={data.skills} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Awards Card (modal stub — page has the real implementation)
// ---------------------------------------------------------------------------

// TODO: Replace with API data when awards field is available
function AwardsCard() {
  return (
    <div className="rounded-[20px] border border-black-50 bg-white p-5 motion-safe:transition-shadow hover:shadow-sm">
      {/* Header */}
      <h4 className="text-body-xl font-semibold text-secondary">Awards</h4>

      {/* Empty state */}
      <p className="mt-3 text-body-sm text-grey">No awards data available.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error State
// ---------------------------------------------------------------------------

function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12">
      <p className="text-body-sm text-grey">
        Failed to load candidate details.
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="cursor-pointer rounded-full border border-black-100 px-4 py-2 text-body-md font-bold tracking-tight text-black-500 motion-safe:transition-[background-color,transform] motion-safe:duration-150 hover:bg-grey-25 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          Retry
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile Tab Content
// ---------------------------------------------------------------------------

function ProfileTabContent({
  data,
  candidate,
  onContactAction,
  onStatusChange,
  isStatusUpdating,
}: {
  data: CandidateDetail;
  candidate: MyCandidateFromAPI;
  onContactAction: (message: string) => void;
  onStatusChange?: (newStatus: CandidateStatusEnum) => void;
  isStatusUpdating?: boolean;
}) {
  // Collect right-panel sections — always show all sections with empty states
  const rightSections: React.ReactNode[] = [];

  // Always show Match to Job Description
  // Prefer profile_matching_summary from detail API, fall back to list-level data
  rightSections.push(
    <MatchToJobCard
      key="match"
      profileMatchingSummary={
        data.profile_matching_summary ?? candidate.profile_matching_summary
      }
    />
  );

  // Always show Work Experience
  rightSections.push(
    <WorkExperienceCard
      key="work-experience"
      items={data.employment_history_items}
    />
  );

  // Always show Education
  rightSections.push(
    <EducationCard key="education" items={data.education_items} />
  );

  // Always show Awards (stubbed with empty state for now)
  rightSections.push(<AwardsCard key="awards" />);

  return (
    <div className="flex flex-col gap-2.5 lg:flex-row lg:flex-nowrap">
      {/* LEFT PANEL - 356px fixed on lg+, full width on mobile */}
      <div className="w-full shrink-0 lg:w-[356px]">
        <AnimatedSection index={0}>
          <ProfileWidgetCard
            data={data}
            candidate={candidate}
            onContactAction={onContactAction}
            onStatusChange={onStatusChange}
            isStatusUpdating={isStatusUpdating}
          />
        </AnimatedSection>
      </div>

      {/* RIGHT PANEL - fills remaining space on lg+, full width on mobile */}
      <div className="flex min-w-0 flex-1 flex-col gap-[15px]">
        {rightSections.map((section, index) => {
          const sectionKey = (section as React.ReactElement).key ?? index;
          return (
            <AnimatedSection key={sectionKey} index={index + 1}>
              {section}
            </AnimatedSection>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function CandidateDetailContent({
  candidate,
  jdPath,
  onContactAction,
  onStatusChange,
  isStatusUpdating,
  className,
}: CandidateDetailContentProps) {
  const [activeTab, setActiveTab] = useState<TabId>("profile");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["candidates", candidate.candidate_id, jdPath] as const,
    queryFn: ({ signal }) => {
      const params = jdPath ? `?jd_path=${encodeURIComponent(jdPath)}` : "";
      return apiClient.get<{ data: CandidateDetail }>(
        `/candidates/${candidate.candidate_id}${params}`,
        { signal }
      );
    },
    // Snapshot feature temporarily disabled — re-enable by restoring
    // mergeSnapshot wrap below + the SnapshotBanner block in the JSX.
    select: (res) => res.data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleRetry = useCallback(() => refetch(), [refetch]);

  return (
    <div
      className={`flex flex-col gap-5 ${className ?? ""}`}
      role="region"
      aria-label={`${toTitleCase(candidate.full_name)} details`}
    >
      {/* Snapshot banner — temporarily disabled. Re-enable by uncommenting:
          {jdPath && data?.candidate_snapshot_at && (
            <SnapshotBanner asOf={data.candidate_snapshot_at} />
          )}
      */}

      {/* Tab Bar */}
      <TabBar
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as TabId)}
        ariaLabel="Candidate detail tabs"
      />

      {/* Tab Panel */}
      <div
        id={`tabpanel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        className="candidate-scrollbar flex-1"
      >
        {activeTab === "profile" ? (
          isLoading ? (
            <CandidateDetailSkeleton />
          ) : error ? (
            <ErrorState onRetry={handleRetry} />
          ) : data ? (
            <ProfileTabContent
              data={data}
              candidate={candidate}
              onContactAction={onContactAction ?? (() => {})}
              onStatusChange={onStatusChange}
              isStatusUpdating={isStatusUpdating}
            />
          ) : null
        ) : activeTab === "resume" ? (
          <CvViewer
            candidateId={candidate.candidate_id}
            candidateName={toTitleCase(candidate.full_name)}
          />
        ) : activeTab === "activities" ? (
          <ActivitiesLog candidateStatusId={candidate.id} />
        ) : null}
      </div>
    </div>
  );
}
